'use client';

import type React from 'react';
import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';

import { cn } from '@skybeam/shared-utils';
import { Button } from '@simulmedia/ui-kit';

import { ChevronLeft, ChevronRight } from 'lucide-react';

type CardType = {
  imageSrc: string;
  link?: string;
  text: string;
  title: string;
};

interface IndustryCardsCarouselProps {
  cards: CardType[];
  mobileCards?: CardType[];
}

const CARD_WIDTH = 555;
const CARD_GAP = 36;

const Card: React.FC<CardType> = ({ imageSrc, link, text, title }) => {
  return (
    <div
      className="tablet:gap-y-9 flex flex-col gap-y-6"
      data-test="industry-card"
    >
      <div className="laptop:hidden flex flex-col gap-y-4">
        <div className="font-inter text-2xl font-semibold text-zinc-950">
          {title}
        </div>
        <div className="tablet:max-w-[518px] text-base text-amber-600">
          {text}
        </div>
        {link && (
          <Link href={link} target="_blank">
            <Button className="rounded-lg px-6 text-base" variant="primary">
              Learn more
            </Button>
          </Link>
        )}
      </div>
      <div
        className="tablet:bg-bottom desktop:size-[555px] tablet:h-115 laptop:h-auto desktop:shrink-0 laptop:p-16.5 desktop:p-10.5 desktop:gap-y-2.5 laptop:gap-y-6.5 laptop:aspect-square relative flex aspect-13/11 w-full min-w-[272px] flex-col rounded-2xl border border-gray-100 bg-cover bg-center bg-no-repeat shadow-md"
        style={{
          backgroundImage: `url(${imageSrc}), linear-gradient(to bottom, rgb(244 248 255), rgb(153 193 255 / 0.5))`,
        }}
      >
        <div className="laptop:flex desktop:gap-y-[5px] laptop:gap-y-2 hidden flex-col">
          <div className="leading-1.2 desktop:text-[28px] laptop:text-4xl font-semibold">
            {title}
          </div>
          <div className="desktop:text-base laptop:text-xl desktop:max-w-[357px] laptop:max-w-[566px] text-gray-600">
            {text}
          </div>
        </div>
        {link && (
          <Link
            href={link}
            target="_blank"
            data-test={`industry-card-link-${link.replace(/^\/industry\//, '')}`}
            className="laptop:block hidden w-fit text-base font-bold text-blue-700 hover:underline"
          >
            Learn more
          </Link>
        )}
      </div>
    </div>
  );
};

export const IndustryCardsCarousel: React.FC<IndustryCardsCarouselProps> = ({
  cards,
  mobileCards,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const [showButtons, setShowButtons] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ scrollLeft: number; x: number } | null>(null);
  const hasMovedRef = useRef(false);

  const cardWithGap = CARD_WIDTH + CARD_GAP;

  const updateButtonVisibility = () => {
    const el = containerRef.current;
    if (!el) return;

    const { clientWidth, scrollLeft, scrollWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    const threshold = 1; // Small threshold to account for rounding

    const allCardsFit = scrollWidth <= clientWidth;
    setShowButtons(!allCardsFit);

    if (allCardsFit) {
      setShowLeftButton(false);
      setShowRightButton(false);
      return;
    }

    setShowLeftButton(scrollLeft > threshold);
    setShowRightButton(scrollLeft < maxScroll - threshold);
  };

  const setInitialScrollPosition = () => {
    const el = containerRef.current;
    if (!el) return;

    const viewportWidth = el.clientWidth;

    const cards2And3Width = 2 * cardWithGap;
    const remainingSpace = Math.max(viewportWidth - cards2And3Width, 0);
    const partialCardWidth = remainingSpace / 2;

    const offset = cardWithGap - partialCardWidth;
    const initialScroll = offset;

    el.scrollLeft = initialScroll;
    updateButtonVisibility();
  };

  const slide = (direction: 'left' | 'right') => {
    if (!containerRef.current) return;

    const el = containerRef.current;
    const { clientWidth, scrollLeft, scrollWidth } = el;
    const step = cardWithGap;
    const maxScroll = scrollWidth - clientWidth;

    let target: number;
    if (direction === 'right') {
      target = Math.min(scrollLeft + step, maxScroll);
    } else {
      target = Math.max(scrollLeft - step, 0);
    }

    el.scrollTo({ left: target, behavior: 'smooth' });
  };

  const handleScroll = () => {
    updateButtonVisibility();
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!el) return;

    const target = e.target as HTMLElement;
    if (
      target.closest('a') ||
      target.closest('button') ||
      target.closest('[role="button"]')
    ) {
      return;
    }

    setIsDragging(true);
    hasMovedRef.current = false;
    dragStartRef.current = {
      scrollLeft: el.scrollLeft,
      x: e.clientX,
    };
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';
    e.preventDefault();
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !dragStartRef.current || !containerRef.current) return;

    const el = containerRef.current;
    const deltaX = e.clientX - dragStartRef.current.x;

    if (Math.abs(deltaX) > 5) {
      hasMovedRef.current = true;
    }

    el.scrollLeft = dragStartRef.current.scrollLeft - deltaX;
  };

  const handleMouseUp = (e?: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;

    if (isDragging && hasMovedRef.current && e) {
      const target = e.target as HTMLElement;
      if (target.closest('a')) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    setIsDragging(false);
    dragStartRef.current = null;
    hasMovedRef.current = false;
    containerRef.current.style.cursor = 'grab';
    containerRef.current.style.userSelect = '';
  };

  const handleMouseLeave = () => {
    handleMouseUp();
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const timer = setTimeout(() => {
      setInitialScrollPosition();
    }, 100);

    const ro = new ResizeObserver(() => {
      setInitialScrollPosition();
    });
    ro.observe(el);

    return () => {
      clearTimeout(timer);
      ro.disconnect();
    };
  }, [setInitialScrollPosition]);

  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging || !dragStartRef.current || !containerRef.current) return;

      const el = containerRef.current;
      const deltaX = e.clientX - dragStartRef.current.x;

      if (Math.abs(deltaX) > 5) {
        hasMovedRef.current = true;
      }

      el.scrollLeft = dragStartRef.current.scrollLeft - deltaX;
    };

    const handleGlobalMouseUp = () => {
      if (!containerRef.current) return;

      setIsDragging(false);
      dragStartRef.current = null;
      hasMovedRef.current = false;
      containerRef.current.style.cursor = 'grab';
      containerRef.current.style.userSelect = '';
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging]);

  const mobileCardsToRender = mobileCards || cards;

  return (
    <>
      <div className="desktop:hidden flex w-full flex-col gap-21">
        {mobileCardsToRender.map((card, index) => (
          <Card key={`${card.title}-${index}`} {...card} />
        ))}
      </div>

      <div className="desktop:flex hidden w-full flex-col items-center gap-9">
        <div
          ref={containerRef}
          data-test="industry-cards-carousel"
          className="w-full cursor-grab overflow-x-auto overflow-y-hidden [-ms-overflow-style:none] [scrollbar-width:none] active:cursor-grabbing [&::-webkit-scrollbar]:hidden"
          onScroll={handleScroll}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <div
            className="flex gap-9 pr-4 pl-4"
            data-test="industry-cards-track"
          >
            {cards.map((card, index) => (
              <Card key={`${card.title}-${index}`} {...card} />
            ))}
          </div>
        </div>
        {showButtons && (
          <div className="relative z-10 flex gap-4">
            <button
              type="button"
              onClick={() => slide('left')}
              data-test="industry-cards-prev-btn"
              className={cn(
                'flex cursor-pointer items-center justify-center rounded-lg bg-blue-100 p-3 shadow-[0px_1px_2px_0px_rgba(27,27,27,0.05)] transition-all hover:bg-blue-200 hover:shadow-[0px_2px_4px_0px_rgba(27,27,27,0.1)]',
                !showLeftButton && 'invisible',
              )}
              aria-label="Previous card"
            >
              <ChevronLeft className="size-5 text-blue-700" />
            </button>
            <button
              type="button"
              onClick={() => slide('right')}
              data-test="industry-cards-next-btn"
              className={cn(
                'flex cursor-pointer items-center justify-center rounded-lg bg-blue-100 p-3 shadow-[0px_1px_2px_0px_rgba(27,27,27,0.05)] transition-all hover:bg-blue-200 hover:shadow-[0px_2px_4px_0px_rgba(27,27,27,0.1)]',
                !showRightButton && 'invisible',
              )}
              aria-label="Next card"
            >
              <ChevronRight className="size-5 text-blue-700" />
            </button>
          </div>
        )}
      </div>
    </>
  );
};
